import { useRef } from 'react';
import imageCompression from 'browser-image-compression';
import { CiCircleMinus } from 'react-icons/ci';
import { PORTFOLIO_CATEGORIES } from '../../../minister/pages/ministerInfo/ministerInfoTypes';
import type { PortfolioCategory, PortfolioItem, SelfIntroItem } from '../../../minister/pages/ministerInfo/ministerInfoTypes';
import { portfolioImageSrc } from '../../../minister/pages/ministerInfo/ministerInfoUtils';

// 이력서 수정 화면의 자기소개서 / 사역 포트폴리오 입력 섹션

export const RESUME_SECTION_IDS = {
  intro: 'resume-intro',
  portfolio: 'resume-portfolio',
} as const;

export const DEFAULT_INTRO_QUESTIONS = [
  '소명과 신앙고백',
  '사역 경험과 강점',
  '사역 철학과 비전',
  '교회와 함께 이루고 싶은 사역',
];

export function withDefaultIntroQuestions(items: SelfIntroItem[] | undefined): SelfIntroItem[] {
  if (items && items.length > 0) return items;
  return DEFAULT_INTRO_QUESTIONS.map((question) => ({ question, answer: '' }));
}

// 편집 중인 포트폴리오 항목 — 새로 고른 사진은 저장 시 업로드
export interface PortfolioEditItem extends PortfolioItem {
  file?: File;
  preview?: string;
}

const emptyPortfolioItem = (category: PortfolioCategory): PortfolioEditItem => ({
  category,
  title: '',
  church: '',
  date: '',
  description: '',
  link: '',
  image: '',
});

// 유튜브 링크면 썸네일 주소를 만들어 준다
export function youtubeThumbnail(link: string) {
  const match = link.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/))([\w-]{11})/);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : '';
}

// 저장 요청용 — 새 사진은 files 에 담고 image 를 "new:<순번>" 으로 치환
export function buildPortfolioPayload(items: PortfolioEditItem[]) {
  const files: File[] = [];
  const payload: PortfolioItem[] = items.map(({ file, preview, ...item }) => {
    if (file) {
      files.push(file);
      return { ...item, image: `new:${files.length - 1}` };
    }
    if (!item.image && item.category !== '사역사진') {
      return { ...item, image: youtubeThumbnail(item.link) };
    }
    return item;
  });
  return { files, payload };
}

// 저장 전에 알려줄 미완성 포트폴리오 항목
export function findIncompletePortfolio(items: PortfolioEditItem[]) {
  return items.findIndex((item) => {
    const hasAnything = item.title || item.link || item.image || item.file || item.description;
    if (!hasAnything) return false;
    if (!item.title.trim()) return true;
    return item.category === '사역사진' ? !(item.image || item.file) : !item.link.trim();
  });
}

const removeBtnStyle = { background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: 'var(--r)', padding: '5px 10px', cursor: 'pointer' };
const addBtnStyle = { padding: '10px 20px', background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: 'var(--r)', cursor: 'pointer' };
const guideTextStyle = { fontSize: '15px', color: 'var(--ink-3)', lineHeight: 1.5, marginBottom: '16px' };

export function SelfIntroductionEdit({
  items,
  onChange,
}: {
  items: SelfIntroItem[];
  onChange: (items: SelfIntroItem[]) => void;
}) {
  const update = (index: number, patch: Partial<SelfIntroItem>) => {
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  return (
    <section id={RESUME_SECTION_IDS.intro}>
      <h1 className='section_title'>자기소개서</h1>
      <p style={guideTextStyle}>
        답변을 작성한 문항만 저장됩니다. 문항 제목은 자유롭게 바꿀 수 있어요.
      </p>
      <div className='inputCover'>
        {items.map((item, index) => (
          <div key={index} style={{ borderTop: '1px solid var(--rule-2)', padding: '20px 0', marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <input value={item.question} className="inputdefault" type="text" placeholder="문항 제목"
                style={{ flex: 1, minWidth: 0, height: '50px', padding: '0 10px', fontSize: '16px', fontWeight: 600, border: '1px solid var(--rule-2)', borderRadius: 'var(--r)', boxSizing: 'border-box' }}
                onChange={(e) => update(index, { question: e.target.value })} />
              <button type="button" onClick={() => onChange(items.filter((_, i) => i !== index))} style={removeBtnStyle}>
                <CiCircleMinus size={20} />
              </button>
            </div>
            <textarea value={item.answer} className="textareadefault" rows={6}
              style={{ width: '100%', padding: '12px', border: '1px solid var(--rule-2)', borderRadius: '4px', fontSize: '16px', lineHeight: 1.6, resize: 'vertical', boxSizing: 'border-box' }}
              placeholder="내용을 입력해주세요"
              onChange={(e) => update(index, { answer: e.target.value })} />
          </div>
        ))}
        <button type="button" style={addBtnStyle}
          onClick={() => onChange([...items, { question: '', answer: '' }])}>
          문항 추가
        </button>
      </div>
    </section>
  );
}

export function PortfolioEdit({
  items,
  onChange,
  userAccount,
}: {
  items: PortfolioEditItem[];
  onChange: (items: PortfolioEditItem[]) => void;
  userAccount: string;
}) {
  const fileInputs = useRef<Record<number, HTMLInputElement | null>>({});

  const update = (index: number, patch: Partial<PortfolioEditItem>) => {
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const handleFile = async (index: number, file?: File) => {
    if (!file) return;
    try {
      const resized = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1600 });
      const safeName = resized.name.replace(/[^a-zA-Z0-9._-]/g, '').slice(-20) || 'image.jpg';
      const newFile = new File([resized], `${userAccount.slice(0, 5)}_${safeName}`, { type: file.type });
      update(index, { file: newFile, preview: URL.createObjectURL(newFile) });
    } catch (error) {
      console.error('포트폴리오 이미지 처리 오류:', error);
      alert('이미지를 불러오지 못했습니다.');
    }
  };

  return (
    <section id={RESUME_SECTION_IDS.portfolio}>
      <h1 className='section_title'>사역 포트폴리오</h1>
      <p style={guideTextStyle}>
        설교·찬양 영상은 유튜브 등 영상 링크를, 사역사진은 사진 파일을 등록해주세요.
        유튜브 링크는 썸네일이 자동으로 표시됩니다.
      </p>
      <div className='inputCover'>
        {items.map((item, index) => {
          const isPhoto = item.category === '사역사진';
          const imageSrc = item.preview || (item.image ? portfolioImageSrc(item.image) : '') || (!isPhoto ? youtubeThumbnail(item.link) : '');
          return (
            <div key={index} style={{ borderTop: '1px solid var(--rule-2)', padding: '20px 0', marginBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4>포트폴리오 {index + 1}</h4>
                <button type="button" onClick={() => onChange(items.filter((_, i) => i !== index))} style={removeBtnStyle}>
                  <CiCircleMinus size={20} />
                </button>
              </div>
              <div className="inputBox">
                <h3 className='title'><p>구분</p></h3>
                <div className="inputRow">
                  <div className='checkTextRow'>
                    {PORTFOLIO_CATEGORIES.map((category) => (
                      <div key={category} className='checkTextBox' onClick={() => update(index, { category })}>
                        <input className="checkbox" type="radio" name={`portfolio-category-${index}`}
                          checked={item.category === category} readOnly />
                        <span className="checkmark" />
                        <div className='imgtextBox'>
                          <p className={item.category === category ? 'selected' : ''}>{category}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="inputCoverTwoColumns">
                <div className="inputBox">
                  <h3 className='title'><p>제목 *</p></h3>
                  <div className="inputRow">
                    <input value={item.title} className="inputdefault" type="text"
                      placeholder={isPhoto ? '예: 여름 청년부 수련회' : '예: 주일 청년예배 설교'}
                      onChange={(e) => update(index, { title: e.target.value })} />
                  </div>
                </div>
                <div className="inputBox">
                  <h3 className='title'><p>교회 / 날짜</p></h3>
                  <div className="inputRow" style={{ display: 'flex', gap: '8px' }}>
                    <input value={item.church} className="inputdefault" type="text" placeholder="교회명"
                      onChange={(e) => update(index, { church: e.target.value })} />
                    <input value={item.date} className="inputdefault" type="text" placeholder="예: 2026.08"
                      style={{ maxWidth: '130px' }}
                      onChange={(e) => update(index, { date: e.target.value })} />
                  </div>
                </div>
              </div>
              {!isPhoto && (
                <div className="inputBox">
                  <h3 className='title'><p>영상 링크 *</p></h3>
                  <div className="inputRow">
                    <input value={item.link} className="inputdefault" type="text" placeholder="https://www.youtube.com/watch?v=..."
                      onChange={(e) => update(index, { link: e.target.value })} />
                  </div>
                </div>
              )}
              <div className="inputBox">
                <h3 className='title'><p>{isPhoto ? '사진 *' : '썸네일 (선택)'}</p></h3>
                <div className="inputRow" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  {imageSrc && (
                    <img src={imageSrc} alt="" style={{ width: '160px', aspectRatio: isPhoto ? '4 / 3' : '16 / 9', objectFit: 'cover', borderRadius: '4px', border: '1px solid var(--rule)' }} />
                  )}
                  <input ref={(el) => { fileInputs.current[index] = el; }} type="file" accept="image/jpeg,image/png,image/gif"
                    style={{ display: 'none' }}
                    onChange={(e) => { handleFile(index, e.target.files?.[0]); e.target.value = ''; }} />
                  <button type="button" style={{ ...addBtnStyle, padding: '8px 16px' }}
                    onClick={() => fileInputs.current[index]?.click()}>
                    {imageSrc ? '사진 변경' : '사진 선택'}
                  </button>
                </div>
              </div>
              <div className="inputBox">
                <h3 className='title'><p>설명</p></h3>
                <div className="inputRow">
                  <input value={item.description} className="inputdefault" type="text" placeholder="간단한 설명"
                    onChange={(e) => update(index, { description: e.target.value })} />
                </div>
              </div>
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {PORTFOLIO_CATEGORIES.map((category) => (
            <button key={category} type="button" style={addBtnStyle}
              onClick={() => onChange([...items, emptyPortfolioItem(category)])}>
              {category} 추가
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
